import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CreditCard,
  Plus,
  Save,
  Trash2,
  CheckCircle2,
  EyeOff,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InlineEditorShell } from "@/components/InlineEditorShell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import { toast } from "sonner";

const FEATURE_KEYS = [
  "all_codecs",
  "failover_routing",
  "per_did_caller_id",
  "white_label",
  "priority_support",
  "api_access",
];

const LIMIT_KEYS = [
  "concurrent_calls",
  "max_users",
  "max_trunks",
  "max_domains",
  "cdr_retention_days",
];

const BILLING_METRICS = [
  { value: "flat", label: "Flat monthly fee" },
  { value: "per_extension", label: "Per extension (Vodia-style)" },
  { value: "per_concurrent_call", label: "Per concurrent call (wholesale)" },
];

const blankPlan = () => ({
  slug: "",
  name: "",
  description: "",
  billing_metric: "per_extension",
  price_per_unit_usd: 3.0,
  min_monthly_usd: 0,
  volume_discount_tiers: [],
  price_monthly_usd: 0,
  price_yearly_usd: 0,
  limits: Object.fromEntries(LIMIT_KEYS.map((k) => [k, 0])),
  features: Object.fromEntries(FEATURE_KEYS.map((k) => [k, false])),
  public: true,
  sort_order: 50,
  stripe_price_id: "",
});

export default function PlatformPlans() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/plans");
      setPlans(r.data.plans || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/platform/plans/${editing.id}`, editing);
      } else {
        await api.post("/platform/plans", editing);
      }
      toast.success(t("plans.saved", "Plan saved"));
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (
      !window.confirm(
        t("plans.confirmDelete", "Delete plan '{{name}}'?", { name: p.name })
      )
    ) {
      return;
    }
    try {
      await api.delete(`/platform/plans/${p.id}`);
      toast.success(t("plans.deleted", "Plan deleted"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Phase N+ — 3CX/Vodia-style inline editor takes over the whole page.
  if (editing) {
    return (
      <InlineEditorShell
        crumb={t("plans.title", "Plans")}
        title={editing.id ? t("plans.edit", "Edit plan") : t("plans.create", "New plan")}
        onCancel={() => setEditing(null)}
        onSave={save}
        saveDisabled={saving || !editing.name?.trim() || !editing.slug?.trim()}
        maxWidth="max-w-2xl"
        testidRoot="plan-editor"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("plans.name", "Name")}</Label>
              <Input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                data-testid="plan-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">{t("plans.slug", "Slug")}</Label>
              <Input
                value={editing.slug}
                onChange={(e) =>
                  setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })
                }
                disabled={!!editing.id}
                data-testid="plan-slug-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t("plans.descLabel", "Description")}</Label>
            <Input
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            />
          </div>

          <div className="rounded-md border border-border bg-muted/20 p-3 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("plans.billingTitle", "Billing")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <Label className="text-xs">{t("plans.metric", "Metric")}</Label>
                <Select
                  value={editing.billing_metric || "flat"}
                  onValueChange={(v) => setEditing({ ...editing, billing_metric: v })}
                >
                  <SelectTrigger data-testid="plan-metric">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_METRICS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editing.billing_metric === "flat" ? (
                <>
                  <div>
                    <Label className="text-xs">{t("plans.priceMo", "$/mo")}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editing.price_monthly_usd}
                      onChange={(e) =>
                        setEditing({ ...editing, price_monthly_usd: Number(e.target.value) })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t("plans.priceYr", "$/yr")}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editing.price_yearly_usd}
                      onChange={(e) =>
                        setEditing({ ...editing, price_yearly_usd: Number(e.target.value) })
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-xs">
                      {editing.billing_metric === "per_extension"
                        ? t("plans.perExt", "$ per extension / mo")
                        : t("plans.perCc", "$ per concurrent call / mo")}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editing.price_per_unit_usd ?? 0}
                      onChange={(e) =>
                        setEditing({ ...editing, price_per_unit_usd: Number(e.target.value) })
                      }
                      data-testid="plan-per-unit"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t("plans.minMo", "Minimum / mo")}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editing.min_monthly_usd ?? 0}
                      onChange={(e) =>
                        setEditing({ ...editing, min_monthly_usd: Number(e.target.value) })
                      }
                      data-testid="plan-min-monthly"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Phase E3 — Stripe Price ID. Optional; only plans with this
                set are eligible for self-serve Stripe Checkout. */}
            <div>
              <Label className="text-xs">
                {t("plans.stripePriceId", "Stripe Price ID")}
                <span className="text-muted-foreground/70 font-normal ml-1">
                  {t("plans.stripePriceIdHint", "(optional — enables self-serve Checkout)")}
                </span>
              </Label>
              <Input
                value={editing.stripe_price_id || ""}
                onChange={(e) =>
                  setEditing({ ...editing, stripe_price_id: e.target.value.trim() })
                }
                placeholder="price_1ABCxxxxxxxxxxxx"
                data-testid="plan-stripe-price-id"
              />
            </div>

            {editing.billing_metric !== "flat" && (
              <div>
                <Label className="text-xs mb-1.5 block">
                  {t("plans.volumeTiersTitle", "Volume discount tiers")}
                </Label>
                <div className="space-y-1.5">
                  {(editing.volume_discount_tiers || []).map((tier, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-xs"
                      data-testid={`volume-tier-${i}`}
                    >
                      <span className="text-muted-foreground">
                        {t("plans.atOrAbove", "At or above")}
                      </span>
                      <Input
                        type="number"
                        min="1"
                        className="w-24"
                        value={tier.threshold}
                        onChange={(e) => {
                          const tt = [...(editing.volume_discount_tiers || [])];
                          tt[i] = { ...tt[i], threshold: Number(e.target.value) };
                          setEditing({ ...editing, volume_discount_tiers: tt });
                        }}
                      />
                      <span className="text-muted-foreground">
                        {editing.billing_metric === "per_extension" ? "ext" : "cc"} →
                      </span>
                      <Input
                        type="number"
                        min="1"
                        max="99"
                        className="w-20"
                        value={tier.percent}
                        onChange={(e) => {
                          const tt = [...(editing.volume_discount_tiers || [])];
                          tt[i] = { ...tt[i], percent: Number(e.target.value) };
                          setEditing({ ...editing, volume_discount_tiers: tt });
                        }}
                      />
                      <span className="text-muted-foreground">% off</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-[hsl(var(--danger))]"
                        onClick={() => {
                          const tt = [...(editing.volume_discount_tiers || [])];
                          tt.splice(i, 1);
                          setEditing({ ...editing, volume_discount_tiers: tt });
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() => {
                      const tt = [...(editing.volume_discount_tiers || [])];
                      const last = tt[tt.length - 1];
                      tt.push({
                        threshold: last ? Number(last.threshold) + 50 : 26,
                        percent: last ? Math.min(99, Number(last.percent) + 5) : 5,
                      });
                      setEditing({ ...editing, volume_discount_tiers: tt });
                    }}
                    data-testid="add-volume-tier"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t("plans.addTier", "Add tier")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("plans.sortOrder", "Sort")}</Label>
              <Input
                type="number"
                value={editing.sort_order}
                onChange={(e) =>
                  setEditing({ ...editing, sort_order: Number(e.target.value) })
                }
              />
            </div>
            <div>
              <Label className="text-xs">{t("plans.priceYrFloor", "$/yr (floor for marketing)")}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editing.price_yearly_usd}
                onChange={(e) =>
                  setEditing({ ...editing, price_yearly_usd: Number(e.target.value) })
                }
              />
            </div>
          </div>

          <div>
            <Label className="text-xs mb-1.5 block">
              {t("plans.limitsTitle", "Limits (0 = unlimited)")}
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {LIMIT_KEYS.map((k) => (
                <div key={k}>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t(`plans.limit.${k}`, k.replace(/_/g, " "))}
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={editing.limits?.[k] ?? 0}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        limits: {
                          ...editing.limits,
                          [k]: Number(e.target.value),
                        },
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs mb-1.5 block">
              {t("plans.featuresTitle", "Features included")}
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {FEATURE_KEYS.map((k) => (
                <label
                  key={k}
                  className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-xs"
                >
                  <span>{t(`plans.feature.${k}`, k.replace(/_/g, " "))}</span>
                  <Switch
                    checked={!!editing.features?.[k]}
                    onCheckedChange={(v) =>
                      setEditing({
                        ...editing,
                        features: { ...editing.features, [k]: v },
                      })
                    }
                  />
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <span className="text-sm">
              {t("plans.publicLabel", "Show on public pricing page")}
            </span>
            <Switch
              checked={!!editing.public}
              onCheckedChange={(v) => setEditing({ ...editing, public: v })}
            />
          </label>
        </div>
      </InlineEditorShell>
    );
  }

  return (
    <div data-testid="platform-plans-page">
      <PageHeader
        title={t("plans.title", "Plans")}
        subtitle={t(
          "plans.subtitle",
          "Pricing tiers offered to tenants. Concurrent calls is the primary metric. Switching a tenant's plan auto-applies the new limits."
        )}
        icon={CreditCard}
        actions={
          <Button
            onClick={() => setEditing(blankPlan())}
            data-testid="new-plan-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t("plans.newBtn", "New plan")}
          </Button>
        }
      />

      <Card>
        <CardContent className="p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground p-6 text-center">
              {t("common.loading", "Loading…")}
            </p>
          ) : plans.length === 0 ? (
            <EmptyState
              title={t("plans.noPlans", "No plans yet")}
              hint={t("plans.noPlansHint", "Create your first pricing tier.")}
            />
          ) : (
            <Table>
              <TableHeader className="sticky-table-head">
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>{t("plans.name", "Name")}</TableHead>
                  <TableHead>{t("plans.metric", "Metric")}</TableHead>
                  <TableHead className="text-right">{t("plans.rate", "Rate")}</TableHead>
                  <TableHead className="text-right">{t("plans.minMo", "$/mo min")}</TableHead>
                  <TableHead className="text-right">{t("plans.cc", "CC cap")}</TableHead>
                  <TableHead className="text-right">{t("plans.users", "Users cap")}</TableHead>
                  <TableHead>{t("plans.public", "Public")}</TableHead>
                  <TableHead className="text-right">{t("common.actions", "Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((p) => (
                  <TableRow
                    key={p.id}
                    className="row-hover"
                    data-testid={`plan-row-${p.slug}`}
                  >
                    <TableCell className="font-mono text-xs">
                      {p.sort_order}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{p.name}</span>
                        {p.seeded && (
                          <StatusPill kind="muted">baseline</StatusPill>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground line-clamp-1">
                        {p.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusPill kind={
                        p.billing_metric === "per_extension" ? "ok"
                        : p.billing_metric === "per_concurrent_call" ? "warn"
                        : "muted"
                      }>
                        {p.billing_metric === "per_extension" ? "per ext"
                          : p.billing_metric === "per_concurrent_call" ? "per cc"
                          : "flat"}
                      </StatusPill>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {p.billing_metric === "per_extension" && (
                        <>${p.price_per_unit_usd.toFixed(2)}/ext</>
                      )}
                      {p.billing_metric === "per_concurrent_call" && (
                        <>${p.price_per_unit_usd.toFixed(2)}/cc</>
                      )}
                      {p.billing_metric === "flat" && (
                        <>${p.price_monthly_usd.toFixed(0)}/mo</>
                      )}
                      {(p.volume_discount_tiers || []).length > 0 && (
                        <div className="text-[10px] text-muted-foreground">
                          {(p.volume_discount_tiers || []).map(t =>
                            `${t.threshold}+ → -${t.percent}%`
                          ).join(', ')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${p.min_monthly_usd.toFixed(0)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {p.limits.concurrent_calls || "∞"}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {p.limits.max_users || "∞"}
                    </TableCell>
                    <TableCell>
                      {p.public ? (
                        <CheckCircle2 className="h-4 w-4 text-[hsl(var(--ok))]" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing({ ...p })}
                        data-testid={`edit-plan-${p.slug}`}
                      >
                        Edit
                      </Button>
                      {!p.seeded && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[hsl(var(--danger))]"
                          onClick={() => remove(p)}
                          data-testid={`delete-plan-${p.slug}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
